import React, { useEffect, useState } from "react";
import { Button, Flex, ModalV2, Select, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Sizes } from "@jsq/ds/styles";
import {
  docTypeLabel,
  DOC_TYPE_OPTIONS,
  fileIconType,
  type EffectiveDoc,
} from "#utils/classificationGrouping";

export type EditDocumentTypeModalProps = {
  isOpen: boolean;
  doc: EffectiveDoc | null;
  onClose: () => void;
  onConfirm: (doc: EffectiveDoc, documentType: string) => void;
};

/**
 * EditDocumentTypeModal — edits the document type of a single document. The
 * current value is preserved as an option when it falls outside the hardcoded
 * selectable list.
 */
export const EditDocumentTypeModal = ({
  isOpen,
  doc,
  onClose,
  onConfirm,
}: EditDocumentTypeModalProps) => {
  const [selectedType, setSelectedType] = useState<string>("");

  useEffect(() => {
    if (doc) {
      setSelectedType(doc.documentType ?? "");
    }
  }, [doc]);

  if (!doc) {
    return null;
  }

  const currentType = doc.documentType ?? "";
  const inList = DOC_TYPE_OPTIONS.some((o) => o.value === currentType);

  return (
    <ModalV2 show={isOpen} onHide={onClose} size="small" title="Edit document type">
      <ModalV2.Body>
        <Flex flexDirection="column" gap={12}>
          <Flex alignItems="center" gap={Sizes.SM}>
            <Icon type={fileIconType(doc.filename)} variant="solid" aria-hidden="true" />
            <Text asSpan lines={1}>
              {doc.filename}
            </Text>
          </Flex>
          <Select
            value={selectedType}
            onChange={(v) => setSelectedType(String(v))}
            placeholder="Select document type…"
            label="Document type"
            full
          >
            <Select.Section>
              {[
                ...(!inList && currentType
                  ? [
                      <Select.Option key="__current" value={currentType}>
                        {docTypeLabel(currentType)}
                      </Select.Option>,
                    ]
                  : []),
                ...DOC_TYPE_OPTIONS.map((o) => (
                  <Select.Option key={o.value} value={o.value}>
                    {o.label}
                  </Select.Option>
                )),
              ]}
            </Select.Section>
          </Select>
        </Flex>
      </ModalV2.Body>
      <ModalV2.Footer>
        <Flex justifyContent="flex-end" gap={Sizes.SM}>
          <Button variant="neutral" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!selectedType || selectedType === currentType}
            onClick={() => onConfirm(doc, selectedType)}
          >
            Save
          </Button>
        </Flex>
      </ModalV2.Footer>
    </ModalV2>
  );
};
